import { useEffect } from 'react';
import { useAppStore } from '../store/appStore';
import { Database, Star, HardDrive, Image, Video, Music } from 'lucide-react';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

const CATEGORY_LABELS: Record<string, string> = {
  character: '角色',
  scene: '场景',
  camera: '运镜',
  prop: '道具',
  style: '画风',
  shot: '分镜',
  uncategorized: '未分类',
};

export function StatsPage() {
  const { stats, fetchStats } = useAppStore();

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-500">加载统计...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in">
      <h2 className="text-xl font-semibold text-slate-100 mb-6">统计概览</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Database} label="总资产" value={String(stats.total)} color="violet" />
        <StatCard icon={Star} label="收藏" value={String(stats.favorites)} color="amber" />
        <StatCard icon={HardDrive} label="存储占用" value={formatBytes(stats.total_size)} color="blue" />
        <StatCard icon={Image} label="图像" value={String(stats.by_media_type.image || 0)} color="emerald" />
      </div>

      {/* Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Category */}
        <div className="glass-panel p-5">
          <h3 className="text-sm font-medium text-slate-300 mb-4">按分类</h3>
          <div className="space-y-3">
            {Object.entries(stats.by_category).map(([cat, count]) => (
              <div key={cat} className="flex items-center gap-3">
                <span className="text-sm text-slate-400 w-16">{CATEGORY_LABELS[cat] || cat}</span>
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-violet-500/60 rounded-full transition-all duration-500"
                    style={{ width: `${stats.total > 0 ? (count / stats.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-sm text-slate-500 w-8 text-right">{count}</span>
              </div>
            ))}
            {Object.keys(stats.by_category).length === 0 && (
              <p className="text-sm text-slate-600">暂无数据</p>
            )}
          </div>
        </div>

        {/* By Model */}
        <div className="glass-panel p-5">
          <h3 className="text-sm font-medium text-slate-300 mb-4">按模型</h3>
          <div className="space-y-3">
            {Object.entries(stats.by_model).map(([model, count]) => (
              <div key={model} className="flex items-center gap-3">
                <span className="text-sm text-slate-400 w-24 truncate">{model}</span>
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/60 rounded-full transition-all duration-500"
                    style={{ width: `${stats.total > 0 ? (count / stats.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-sm text-slate-500 w-8 text-right">{count}</span>
              </div>
            ))}
            {Object.keys(stats.by_model).length === 0 && (
              <p className="text-sm text-slate-600">暂无数据</p>
            )}
          </div>
        </div>

        {/* By Media Type */}
        <div className="glass-panel p-5 lg:col-span-2">
          <h3 className="text-sm font-medium text-slate-300 mb-4">按媒体类型</h3>
          <div className="flex gap-6">
            {[
              { key: 'image', label: '图像', icon: Image, color: 'text-violet-400' },
              { key: 'video', label: '视频', icon: Video, color: 'text-blue-400' },
              { key: 'audio', label: '音频', icon: Music, color: 'text-emerald-400' },
            ].map((type) => (
              <div key={type.key} className="flex items-center gap-3">
                <type.icon size={20} className={type.color} />
                <div>
                  <p className="text-lg font-semibold text-slate-200">{stats.by_media_type[type.key] || 0}</p>
                  <p className="text-xs text-slate-500">{type.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string; color: string }) {
  const colorMap: Record<string, string> = {
    violet: 'bg-violet-500/10 text-violet-400',
    amber: 'bg-amber-500/10 text-amber-400',
    blue: 'bg-blue-500/10 text-blue-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
  };

  return (
    <div className="glass-panel p-4 flex items-center gap-4">
      <div className={`p-2.5 rounded-lg ${colorMap[color]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-slate-100">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}
